import { useEffect, useState, useContext, useRef } from "react";
import { APIServices } from "../helpers/APIServices";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import { Link, useNavigate } from "react-router-dom";
import config from "../config";
import { NoPhotography, Search } from "@mui/icons-material";
import {
  Button,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
  Pagination,
  LinearProgress,
} from "@mui/material";
import { PartContext } from "../App";
import SimpleBackdrop from "../Components/Backdrop";
import { ScrollContext } from "../Layout";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { scrollToRef } from "../helpers/common";

const Dashboard = () => {
  const pageSize = 30; // Items per page

  const [parts, setPart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const navigate = useNavigate();
  const refLayout = useContext(ScrollContext);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  const getParts = async () => {
    try {
      setLoading(true);
      // Removed setPart([]) and setTotal(0) to prevent UI flashing during pagination

      const response = await APIServices.getPartForDashboard(
        page,
        pageSize,
        debouncedSearch.trim(),
      );
      if (response && response.data) {
        const result = response.data;
        if (result.data) {
          setPart(result.data);
          // Use total from pagination meta, which reflects filtered results
          setTotal(result.meta?.pagination?.total || 0);
        } else {
          setPart([]);
          setTotal(0);
        }
      } else {
        setPart([]);
        setTotal(0);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching parts:", error);
      setPart([]);
      setTotal(0);
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
    // Only reset to page 1 if we are not already on it
    if (page !== 1) {
      setPage(1);
    }
  };

  useEffect(() => {
    getParts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch]);

  const [dialogSelectRevise, setDialogSelectRevise] = useState({
    isOpen: false,
  });
  const [selectedPart, setSelectedPart] = useState();

  const handleClickOnPart = (part) => {
    const thisPart = part.attributes;

    if (thisPart?.old_parts?.data?.length > 0) {
      setSelectedPart(part);
      setDialogSelectRevise((prev) => ({
        ...prev,
        isOpen: true,
      }));
    } else {
      navigate(`category/${part.id}`);
    }
  };

  const refs = useRef([]);
  const idKeepPartId = "scroll-to-partId-dashboard";
  const [partMarkerId] = useState(() => {
    const presistedId = sessionStorage.getItem(idKeepPartId);
    return presistedId ? presistedId : null;
  });

  const keepPartId = (id) => {
    sessionStorage.setItem(idKeepPartId, id);
  };

  useEffect(() => {
    if (partMarkerId) {
      const partIndex = parts.findIndex(
        (part) => part.id.toString() === partMarkerId.toString(),
      );
      console.log("in if", partIndex);
      console.log("refs: ", refs);
      scrollToRef(refs.current[partIndex]);
      sessionStorage.removeItem(idKeepPartId);
    }
  }, [parts]);
  return (
    <div>
      <div
        className={`z-10 fixed bottom-0 right-0 mx-8 my-4 bg-colorSub hover:bg-blue-500 rounded-full p-4 text-white cursor-pointer flex`}
        onClick={() => {
          refLayout.current.scrollTo({ top: 0, behavior: "smooth" });
        }}
      >
        <ArrowUpwardIcon />
      </div>
      {/* Only show full backdrop on initial load when parts are empty */}
      <SimpleBackdrop
        open={loading && parts.length === 0}
        setOpen={setLoading}
      />
      <DialogSelectRevise
        dialogSelectRevise={dialogSelectRevise}
        setDialogSelectRevise={setDialogSelectRevise}
        selectedPart={selectedPart}
      />
      <div className=" text-3xl">Select part</div>
      <div className="mb-2 mt-2 flex">
        <TextField
          value={search}
          onChange={(e) => {
            handleSearch(e.target.value);
          }}
          placeholder="Search by Part Name or Part Code"
          className="flex-1"
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </div>
      <div className="relative mt-4">
        {/* Progress bar on top of cards during pagination/search updates */}
        {loading && parts.length > 0 && (
          <div className="absolute top-0 left-0 right-0 z-20">
            <LinearProgress
              sx={{
                backgroundColor: "#ffccbc",
                "& .MuiLinearProgress-bar": { backgroundColor: "#ff5722" },
              }}
            />
          </div>
        )}
        <div
          className={`flex gap-4 flex-wrap mt-4 transition-opacity duration-300 ${loading ? "opacity-40 pointer-events-none" : "opacity-100"}`}
        >
          {parts.map((part, index) => (
            <Card
              className="drop-shadow cursor-pointer"
              key={`card${index}`}
              sx={{ width: 345 }}
              onClick={() => {
                handleClickOnPart(part);
                keepPartId(part.id);
              }}
              ref={(element) => (refs.current[index] = element)}
            >
              {part.attributes.image.data ? (
                <CardMedia
                  sx={{ height: 250 }}
                  image={`${part.attributes.image.data[0].attributes.url}`}
                />
              ) : (
                <div
                  style={{ height: 250 }}
                  className="bg-gray-100 flex items-center justify-center"
                >
                  <NoPhotography sx={{ fontSize: 80 }} />
                </div>
              )}
              <CardContent>
                <Typography gutterBottom variant="h5">
                  {part.attributes.part_name}
                </Typography>
                <Typography variant="body2">
                  {part.attributes.part_code}
                </Typography>
              </CardContent>
            </Card>
            // </Link>
          ))}
          {/* <TableReport /> */}
        </div>
      </div>
      <div className="flex my-6 mr-10 justify-center">
        {total > 0 && (
          <Pagination
            count={Math.ceil(total / pageSize)}
            page={page}
            onChange={(event, value) => {
              setPage(value);
            }}
            sx={{
              "& .MuiPaginationItem-root": {
                fontSize: "1.25rem",
                color: "#ff5722",
              },
              "& .Mui-selected": {
                backgroundColor: "#ff5722 !important",
                color: "#fff",
              },
              "& .MuiPaginationItem-root:hover": {
                backgroundColor: "#ffccbc",
              },
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Dashboard;

const DialogSelectRevise = ({
  dialogSelectRevise,
  setDialogSelectRevise,
  selectedPart,
}) => {
  const navigate = useNavigate();

  const [oldParts, setOldParts] = useState([]);
  const [selectPartID, setSelectPartID] = useState(`none`);
  const [error, setError] = useState({
    revise: ``,
  });

  const gotoPart = () => {
    if (selectPartID === "none") {
      setError((prev) => ({
        ...prev,
        revise: `กรุณาเลือก Revise`,
      }));
      return;
    }
    navigate(`category/${selectPartID}`);
  };

  const handleClose = () => {
    setDialogSelectRevise((prev) => ({
      ...prev,
      isOpen: false,
    }));
    setSelectPartID(`none`);
  };

  useEffect(() => {
    if (selectedPart && selectedPart.attributes.old_parts) {
      setOldParts([...selectedPart.attributes.old_parts.data, selectedPart]);
    }
  }, [selectedPart]);

  const latestPartId =
    oldParts.length > 0 ? oldParts[oldParts.length - 1].id : "none";

  return (
    <Dialog
      open={dialogSelectRevise.isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth={`sm`}
    >
      <DialogTitle className="bg-greyCustom">กรุณาเลือก Rev.</DialogTitle>
      <DialogContent className="bg-greyCustom">
        <div>
          เนื่องจาก part นี้มีการ Revise กรุณาเลือก Revise ที่ต้องการดูข้อมูล
        </div>
        <div>
          <Select
            className="bg-white"
            label={`test`}
            defaultValue={`none`}
            fullWidth
            error={error.revise !== ``}
            onChange={(e, value) => {
              setSelectPartID(e.target.value);
              setError((prev) => ({
                ...prev,
                revise: ``,
              }));
            }}
          >
            <MenuItem value={`none`}>none</MenuItem>
            {oldParts.map((oldPart, indexOldPart) => {
              return (
                <MenuItem
                  key={`menuitem${indexOldPart}`}
                  value={`${oldPart.id}`}
                >
                  {`${oldPart.attributes?.revise_time}`} -
                  {`${oldPart.attributes?.revise?.data?.attributes?.revisionRecord || "N/A"}`}
                </MenuItem>
              );
            })}
          </Select>
          {error.revise !== "" && (
            <span className={`text-red-500`}>{error.revise}</span>
          )}
        </div>
      </DialogContent>
      <DialogActions className="bg-greyCustom">
        <Button className="bg-white" variant="outlined" onClick={handleClose}>
          ปิด
        </Button>
        <Button className="bg-colorMain" variant="contained" onClick={gotoPart}>
          ยืนยัน
        </Button>
      </DialogActions>
    </Dialog>
  );
};
